import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import {
  CardElement,
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSearchParams } from "react-router-dom";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { getDevTierOverride, useTier } from "../utils/tierContext";
import { isOwner as checkIsOwner } from "../utils/access";
import type { ApiClient, ApiError, SubscriptionStatus, TierLevel } from "../types/UserTypes";
import type {
  BillingSummaryResponse,
  CancelSubscriptionRequest,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutReturnRequest,
  CheckoutReturnResponse,
  DowngradeRequest,
  DowngradeResponse,
  EnterpriseAcknowledgementRequest,
  InvoiceSummaryResponse,
  PaymentMethodSummaryResponse,
  PaymentRetryResponse,
  PlanPriceResponse,
  ScheduledChangeRemovalResponse,
  SeatReconciliationResponse,
  SetDefaultPaymentMethodRequest,
  SetupIntentResponse,
  SubscriptionResponse,
  UpcomingInvoiceResponse,
  UpgradeRequest,
  UpgradeResponse,
  UsageResponse,
} from "../types/apiSchemaAliases";
import Toast from "./Toast";
import styles from "./Billing.module.css";

type UsageBillingBasis = "active" | "total";

interface UsageMetric {
  current: number;
  active: number | null;
  total: number | null;
  max: number | null;
  billingBasis: UsageBillingBasis | null;
}

interface UsageCounts {
  users: UsageMetric;
  clients: UsageMetric;
}

type CheckoutTier = Extract<TierLevel, "core" | "pro">;
type PaidTier = CheckoutTier;
type BillingProvider = "stripe" | "stripe_custom" | "external";
type PlanDialog = { type: "downgrade" | "cancel"; targetTier: Extract<TierLevel, "free_crew" | "core" | "pro"> };
type PaymentSetupSession = { clientSecret: string; publishableKey: string };
type PaymentSetupStartOptions = { staleClientSecret?: string };
type PlanStatusTone = "neutral" | "info" | "warning" | "danger";

interface PlanStatusPresentation {
  tone: PlanStatusTone;
  badgeLabel: string | null;
  headerDetail: string | null;
  headline: string;
  body: string;
  showBanner: boolean;
}

interface CheckoutSession {
  tier: CheckoutTier;
  clientSecret: string;
  publishableKey: string;
  seatQuantity: number;
}

interface PaymentMethodSetupFormProps {
  clientSecret: string;
  onCancel: () => void;
  onError: (message: string) => void;
  onSetupExpired: () => Promise<void>;
  onSuccess: (setupIntentId: string) => Promise<void>;
}

const COMPARISON_FEATURES = [
  { label: "Time & Expense Entry", key: "time_entry", free: true, core: true, pro: true, enterprise: true },
  { label: "Client & Prospect Coding", key: "client_prospect_coding", free: true, core: true, pro: true, enterprise: true },
  { label: "Raw Excel Export", key: "raw_excel_export", free: true, core: true, pro: true, enterprise: true },
  { label: "Receipt Uploads", key: "receipt_uploads", free: false, core: true, pro: true, enterprise: true },
  { label: "Approval Workflow", key: "approval_workflow", free: false, core: "Standard", pro: "Advanced", enterprise: "Advanced" },
  { label: "Invoice Generation", key: "invoice_generation", free: false, core: true, pro: true, enterprise: true },
  { label: "Time Overrides", key: "time_overrides", free: false, core: false, pro: true, enterprise: true },
  { label: "Company Expense Accounts", key: "company_expense_accounts", free: false, core: false, pro: true, enterprise: true },
  { label: "Billing Controls", key: "billing_controls", free: false, core: "Standard", pro: "Advanced", enterprise: "Advanced" },
  { label: "Custom Job Types", key: "custom_job_types", free: false, core: false, pro: true, enterprise: true },
  { label: "Custom Expense Categories", key: "custom_expense_categories", free: false, core: false, pro: true, enterprise: true },
  { label: "Client-Ready Reports", key: "client_ready_reports", free: false, core: false, pro: true, enterprise: true },
  { label: "Audit History", key: "audit_history", free: false, core: false, pro: true, enterprise: true },
  { label: "Custom Report Templates", key: "custom_report_templates", free: false, core: false, pro: "Add-on", enterprise: true },
  { label: "Integrations", key: "integrations", free: false, core: false, pro: "Add-on", enterprise: true },
];

const TIER_ORDER: TierLevel[] = ["free_crew", "core", "pro", "enterprise"];
const TIER_LABELS: Record<TierLevel, string> = {
  free_crew: "FreeCrew",
  core: "Core",
  pro: "Pro",
  enterprise: "Enterprise",
};
const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "incomplete",
  "incomplete_expired",
  "canceled",
];

const CHECKOUT_POLL_ATTEMPTS = 12;
const CHECKOUT_POLL_INTERVAL_MS = 2000;
const CHECKOUT_PAYMENT_METHOD_POLL_ATTEMPTS = 6;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "--";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
    year: "numeric",
  });
}

function formatMoney(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(amount);
}

function hasMoneyAmount(amount: number | null | undefined): amount is number {
  return amount !== null && amount !== undefined;
}

function hasStripeSubscriptionState(summary: BillingSummaryResponse | null | undefined): boolean {
  if (!summary || normalizeTier(summary.tier) === "free_crew") return false;
  return Boolean(
    summary.currentPeriodStart ||
    summary.currentPeriodEnd ||
    summary.cancelAtPeriodEnd ||
    summary.canceledAt ||
    summary.gracePeriodEnd ||
    summary.scheduledChange,
  );
}

function normalizeBillingProvider(value: string | null | undefined): BillingProvider | null {
  return value === "stripe" || value === "stripe_custom" || value === "external" ? value : null;
}

function isStripeBillingProvider(value: BillingProvider | null): boolean {
  return value === "stripe" || value === "stripe_custom";
}

function isTierLevel(value: string | null | undefined): value is TierLevel {
  return value === "free_crew" || value === "core" || value === "pro" || value === "enterprise";
}

function isCheckoutTier(value: string | null | undefined): value is CheckoutTier {
  return value === "core" || value === "pro";
}

function normalizeTier(value: string | null | undefined): TierLevel | null {
  return isTierLevel(value) ? value : null;
}

function normalizeSubscriptionStatus(value: string | null | undefined): SubscriptionStatus | null {
  return SUBSCRIPTION_STATUSES.includes(value as SubscriptionStatus)
    ? value as SubscriptionStatus
    : null;
}

function statusToneClass(tone: PlanStatusTone): string {
  if (tone === "danger") return styles["status-danger"];
  if (tone === "warning") return styles["status-warning"];
  if (tone === "info") return styles["status-info"];
  return styles["status-neutral"];
}

function statusLabel(status: SubscriptionStatus): string {
  return status.replace(/_/g, " ");
}

function periodLabel(status: SubscriptionStatus, cancelAtPeriodEnd: boolean): string {
  if (cancelAtPeriodEnd) return "Cancels";
  if (status === "trialing") return "Trial ends";
  if (status === "canceled") return "Access until";
  return "Renews";
}

function getPlanStatusPresentation({
  status,
  paymentHealth,
  cancelAtPeriodEnd,
  needsPlanActivation,
  tierLabel,
  periodEnd,
  gracePeriodEnd,
}: {
  status: SubscriptionStatus;
  paymentHealth: string | null | undefined;
  cancelAtPeriodEnd: boolean;
  needsPlanActivation: boolean;
  tierLabel: string;
  periodEnd: string | null | undefined;
  gracePeriodEnd: string | null | undefined;
}): PlanStatusPresentation {
  const formattedPeriodEnd = formatDate(periodEnd);
  const formattedGracePeriodEnd = formatDate(gracePeriodEnd);

  if (needsPlanActivation) {
    return {
      tone: "warning",
      badgeLabel: "Activation needed",
      headerDetail: "Billing not active",
      headline: `Activate billing for ${tierLabel}`,
      body: `Your tenant has ${tierLabel} access, but there is no active Stripe subscription attached. Activate billing to manage paid plan changes.`,
      showBanner: true,
    };
  }

  if (cancelAtPeriodEnd) {
    return {
      tone: "info",
      badgeLabel: "Cancel scheduled",
      headerDetail: periodEnd ? `Ends ${formattedPeriodEnd}` : "Cancel scheduled",
      headline: `${tierLabel} cancellation scheduled`,
      body: periodEnd
        ? `Your ${tierLabel} plan remains active until ${formattedPeriodEnd}. After that, your account switches to FreeCrew.`
        : `Your ${tierLabel} plan is scheduled to switch to FreeCrew.`,
      showBanner: true,
    };
  }

  if (
    status === "past_due" ||
    status === "unpaid" ||
    paymentHealth === "past_due" ||
    paymentHealth === "unpaid"
  ) {
    return {
      tone: "danger",
      badgeLabel: status === "unpaid" || paymentHealth === "unpaid" ? "Unpaid" : "Past due",
      headerDetail: gracePeriodEnd ? `Update by ${formattedGracePeriodEnd}` : null,
      headline: "Payment past due",
      body: gracePeriodEnd
        ? `We could not collect payment for your ${tierLabel} plan. Update your payment method by ${formattedGracePeriodEnd} to avoid losing access.`
        : `We could not collect payment for your ${tierLabel} plan. Update your payment method to keep access active.`,
      showBanner: true,
    };
  }

  if (paymentHealth === "requires_payment_method") {
    return {
      tone: "danger",
      badgeLabel: "Payment method needed",
      headerDetail: "Payment method needed",
      headline: "Add a payment method",
      body: `Your ${tierLabel} plan needs a valid payment method before billing can continue.`,
      showBanner: true,
    };
  }

  if (paymentHealth === "requires_action") {
    return {
      tone: "warning",
      badgeLabel: "Action required",
      headerDetail: "Payment action required",
      headline: "Confirm your payment",
      body: `Your bank needs one more confirmation before your ${tierLabel} payment can complete.`,
      showBanner: true,
    };
  }

  if (status === "incomplete") {
    return {
      tone: "warning",
      badgeLabel: "Checkout incomplete",
      headerDetail: "Checkout incomplete",
      headline: "Finish setting up billing",
      body: `Checkout is not complete yet. Finish setup to activate billing for your ${tierLabel} plan.`,
      showBanner: true,
    };
  }

  if (status === "incomplete_expired") {
    return {
      tone: "neutral",
      badgeLabel: "Checkout expired",
      headerDetail: "Checkout expired",
      headline: "Checkout expired",
      body: "The previous checkout session expired before payment completed. Start checkout again when you are ready.",
      showBanner: true,
    };
  }

  if (status === "canceled") {
    return {
      tone: "neutral",
      badgeLabel: "Canceled",
      headerDetail: periodEnd ? `Access until ${formattedPeriodEnd}` : "Canceled",
      headline: `${tierLabel} subscription canceled`,
      body: periodEnd
        ? `Access continues until ${formattedPeriodEnd}. You can choose a paid plan again any time.`
        : "This paid subscription is canceled. You can choose a paid plan again any time.",
      showBanner: true,
    };
  }

  if (status === "trialing") {
    return {
      tone: "info",
      badgeLabel: "Trial",
      headerDetail: periodEnd ? `Trial ends ${formattedPeriodEnd}` : "Trial active",
      headline: `${tierLabel} trial active`,
      body: periodEnd
        ? `Your trial runs through ${formattedPeriodEnd}. Add a payment method to keep access after the trial.`
        : "Your trial is active. Add a payment method to keep access after the trial.",
      showBanner: true,
    };
  }

  return {
    tone: "neutral",
    badgeLabel: null,
    headerDetail: periodEnd ? `${periodLabel(status, cancelAtPeriodEnd)} ${formattedPeriodEnd}` : null,
    headline: "",
    body: "",
    showBanner: false,
  };
}

function paymentHealthLabel(value: string | null | undefined): string {
  if (!value || value === "none") return "No payment method";
  return value.replace(/_/g, " ");
}

function requiresPaymentAttention(paymentHealth: string | null | undefined, status: SubscriptionStatus): boolean {
  return (
    status === "past_due" ||
    status === "unpaid" ||
    paymentHealth === "past_due" ||
    paymentHealth === "unpaid" ||
    paymentHealth === "requires_payment_method" ||
    paymentHealth === "requires_action"
  );
}

function normalizeUsage(data: UsageResponse): UsageCounts {
  const normalizeMetric = (metric: UsageResponse["users"]): UsageMetric => {
    const current = metric?.current ?? 0;
    const billingBasis =
      metric?.billingBasis === "active" || metric?.billingBasis === "total"
        ? metric.billingBasis
        : null;

    return {
      current,
      active: metric?.active ?? null,
      total: metric?.total ?? null,
      max: metric?.max ?? null,
      billingBasis,
    };
  };

  return {
    users: normalizeMetric(data.users),
    clients: normalizeMetric(data.clients),
  };
}

function usageBasisFor(metric: UsageMetric, tier: TierLevel): UsageBillingBasis {
  if (metric.billingBasis) return metric.billingBasis;
  return tier === "free_crew" ? "total" : "active";
}

function usageKpiLabel(metric: UsageMetric, tier: TierLevel, noun: string): string {
  return `${usageBasisFor(metric, tier) === "total" ? "Total" : "Active"} ${noun}`;
}

function usageKpiValue(metric: UsageMetric, tier: TierLevel, override?: number | null): number {
  if (override !== null && override !== undefined) return override;
  return usageBasisFor(metric, tier) === "total"
    ? metric.total ?? metric.current
    : metric.active ?? metric.current;
}

function tierLimitLabel(max: number | null | undefined, tier: TierLevel, noun: string): string {
  if (max === undefined) return `${TIER_LABELS[tier]} ${noun} limit unavailable`;
  return max === null
    ? `Unlimited ${noun} on ${TIER_LABELS[tier]}`
    : `${max} ${noun} included on ${TIER_LABELS[tier]}`;
}

function userLimitLabel(max: number | null | undefined, tier: TierLevel): string {
  if (max === undefined) return `${TIER_LABELS[tier]} user limit unavailable`;
  if (max === null) return `No user cap on ${TIER_LABELS[tier]}; active users are billed monthly`;
  return tier === "free_crew"
    ? `${max} total users included on ${TIER_LABELS[tier]}`
    : `${max} users included on ${TIER_LABELS[tier]}`;
}

function planBaseMonthlyPrice(planPrice: PlanPriceResponse | undefined): number | null {
  if (!planPrice) return null;
  return planPrice.baseMonthlyPrice ?? null;
}

function planSeatMonthlyPrice(planPrice: PlanPriceResponse | undefined): number | null {
  if (!planPrice) return null;
  return planPrice.seatMonthlyPrice ?? null;
}

function formatPlanPrice(
  amount: number | null | undefined,
  currency: string | null | undefined,
  suffix = "/mo",
  zeroLabel = "Included",
): string {
  if (amount === null || amount === undefined) return "--";
  if (amount === 0) return zeroLabel;
  return `${formatMoney(amount, currency)}${suffix}`;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function scrollElementIntoView(id: string, options?: ScrollIntoViewOptions): void {
  const element = document.getElementById(id);
  if (typeof element?.scrollIntoView === "function") {
    element.scrollIntoView(options);
  }
}

function makeIdempotencyKey(action: string, targetTier?: string | null): string {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `${action}-${targetTier ?? "current"}-${Date.now()}-${suffix}`;
}

function isApiError(error: unknown): error is ApiError {
  return Boolean(error && typeof error === "object" && "status" in error);
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

function paymentMethodErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (error.code === "STRIPE_PAYMENT_METHOD_ERROR") {
      return error.message || "Stripe could not save the payment method. Please try again.";
    }
    if (error.code === "PAYMENT_RECOVERY_FAILED") {
      return error.message || "Payment method updated, but we couldn't retry the overdue payment.";
    }
  }
  return errorMessage(error, fallback);
}

function paymentMethodLabel(paymentMethod: PaymentMethodSummaryResponse | null | undefined): string {
  if (!paymentMethod?.hasPaymentMethod) return "No payment method on file";
  const brand = paymentMethod.brand
    ? paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1).toLowerCase()
    : "Card";
  const last4 = paymentMethod.last4 ? ` **** ${paymentMethod.last4}` : "";
  const expires =
    paymentMethod.expMonth && paymentMethod.expYear
      ? ` - Exp ${String(paymentMethod.expMonth).padStart(2, "0")}/${paymentMethod.expYear}`
      : "";
  return `${brand}${last4}${expires}`;
}

async function confirmStripePaymentIfNeeded(result: {
  paymentIntentClientSecret?: string | null;
  publishableKey?: string | null;
}): Promise<void> {
  if (!result.paymentIntentClientSecret) return;
  if (!result.publishableKey) {
    throw new Error("Additional payment confirmation is required, but Stripe is not configured.");
  }
  const stripe = await loadStripe(result.publishableKey);
  if (!stripe) {
    throw new Error("Unable to load Stripe payment confirmation.");
  }
  const confirmation = await stripe.confirmPayment({
    clientSecret: result.paymentIntentClientSecret,
    confirmParams: {
      return_url: window.location.href,
    },
    redirect: "if_required",
  });
  if (confirmation.error) {
    throw new Error(confirmation.error.message || "Payment confirmation failed.");
  }
}

function PaymentMethodSetupForm({
  clientSecret,
  onCancel,
  onError,
  onSetupExpired,
  onSuccess,
}: PaymentMethodSetupFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [confirmedSetupIntentId, setConfirmedSetupIntentId] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    try {
      if (confirmedSetupIntentId) {
        await onSuccess(confirmedSetupIntentId);
        return;
      }
      if (!stripe || !elements) return;
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Payment card field is not available yet.");
      }
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
      if (result.error) {
        if (result.error.code === "setup_intent_unexpected_state") {
          await onSetupExpired();
          return;
        }
        throw new Error(result.error.message || "Payment method setup failed.");
      }
      if (!result.setupIntent?.id) {
        throw new Error("Payment method setup did not return a setup intent.");
      }
      if (result.setupIntent.status && result.setupIntent.status !== "succeeded") {
        throw new Error("Payment method setup is not complete yet.");
      }
      setConfirmedSetupIntentId(result.setupIntent.id);
      await onSuccess(result.setupIntent.id);
    } catch (err) {
      onError(errorMessage(err, "Payment method setup failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles["payment-setup-form"]} onSubmit={handleSubmit}>
      <div className={styles["payment-setup-copy"]}>
        <span className={styles["payment-setup-title"]}>Enter a new payment method</span>
        <span className={styles["payment-setup-help"]}>
          Your new card will replace the card on file. You won't be charged now.
        </span>
      </div>
      <div className={styles["card-element-shell"]}>
        <CardElement
          options={{
            hidePostalCode: false,
            style: {
              base: {
                color: "#23324a",
                fontFamily: "inherit",
                fontSize: "16px",
                "::placeholder": {
                  color: "#9ca3af",
                },
              },
              invalid: {
                color: "#ef4444",
              },
            },
          }}
        />
      </div>
      <div className={styles["payment-setup-actions"]}>
        <button
          type="button"
          className={styles["secondary-btn"]}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles["primary-btn"]}
          disabled={!stripe || !elements || saving}
        >
          {saving ? "Saving..." : "Save payment method"}
        </button>
      </div>
    </form>
  );
}

const Billing = () => {
  const authRequest = { ...loginRequest };
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    tier,
    subscriptionStatus,
    currentPeriodEnd,
    gracePeriodEnd,
    loading,
    error,
    refreshTier,
  } = useTier();

  const [usage, setUsage] = useState<UsageCounts | null>(null);
  const [prices, setPrices] = useState<PlanPriceResponse[]>([]);
  const [billingSummary, setBillingSummary] = useState<BillingSummaryResponse | null>(null);
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoiceResponse | null>(null);
  const [invoices, setInvoices] = useState<InvoiceSummaryResponse[]>([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSession | null>(null);
  const [paymentSetup, setPaymentSetup] = useState<PaymentSetupSession | null>(null);
  const [planDialog, setPlanDialog] = useState<PlanDialog | null>(null);
  const [enterprisePromptTier, setEnterprisePromptTier] = useState<TierLevel | null>(null);
  const [latestDowngradeImpact, setLatestDowngradeImpact] = useState<DowngradeResponse | null>(null);
  const [enterpriseSubmitting, setEnterpriseSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const autoStartKeyRef = useRef<string | null>(null);
  const checkoutCompleteKeyRef = useRef<string | null>(null);

  const ownerUser = checkIsOwner(Number(user?.roleId));
  const rawRequestedTier = searchParams.get("tier");
  const requestedTier = isCheckoutTier(rawRequestedTier) ? rawRequestedTier : null;
  const checkoutMode = searchParams.get("checkout");
  const checkoutSessionId = searchParams.get("session_id");

  const api = useMemo<ApiClient | null>(() => {
    if (!ownerUser || !token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [ownerUser, token, getToken, activeTenantId, user?.userId]);

  const stripePromise = useMemo(() => {
    if (!checkoutSession?.publishableKey) return null;
    return loadStripe(checkoutSession.publishableKey);
  }, [checkoutSession?.publishableKey]);

  const paymentSetupStripePromise = useMemo(() => {
    if (!paymentSetup?.publishableKey) return null;
    return loadStripe(paymentSetup.publishableKey);
  }, [paymentSetup?.publishableKey]);

  const currentTier = getDevTierOverride() ?? normalizeTier(billingSummary?.tier) ?? tier;
  const currentStatus = normalizeSubscriptionStatus(billingSummary?.status) ?? subscriptionStatus;
  const summaryPeriodStart = billingSummary?.currentPeriodStart;
  const summaryPeriodEnd = billingSummary ? billingSummary.currentPeriodEnd : currentPeriodEnd;
  const summaryGracePeriodEnd = billingSummary ? billingSummary.gracePeriodEnd : gracePeriodEnd;
  const cancelAtPeriodEnd = Boolean(billingSummary?.cancelAtPeriodEnd);
  const currentTierIdx = Math.max(TIER_ORDER.indexOf(currentTier), 0);
  const billingProvider = normalizeBillingProvider(billingSummary?.billingProvider);
  const stripeBillingProvider = isStripeBillingProvider(billingProvider);
  const customStripeBilling = billingProvider === "stripe_custom";
  const externalBilling = billingProvider === "external";
  const unknownBillingProvider = Boolean(billingSummary) && !billingProvider;
  const selfServicePlanChanges = billingProvider === "stripe";
  const hasPaidSubscription = currentTier !== "free_crew" && currentStatus !== "incomplete_expired";
  const paidAndStripeBacked =
    hasPaidSubscription &&
    currentStatus !== "canceled" &&
    stripeBillingProvider &&
    hasStripeSubscriptionState(billingSummary);
  const needsPlanActivation = Boolean(
    billingSummary &&
    hasPaidSubscription &&
    currentStatus !== "canceled" &&
    selfServicePlanChanges &&
    !hasStripeSubscriptionState(billingSummary),
  );
  const paymentNeedsAttention = requiresPaymentAttention(billingSummary?.paymentHealth, currentStatus);

  const clearCheckoutSearch = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    next.delete("tier");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadBillingDetails = useCallback(
    async (options?: { silent?: boolean }): Promise<BillingSummaryResponse | null> => {
      if (!api || !ownerUser) {
        setBillingLoading(false);
        return null;
      }

      if (!options?.silent) {
        setBillingLoading(true);
      }
      setBillingError(false);

      try {
        const [usageResult, pricesResult, summaryResult] = await Promise.allSettled([
          api.get<UsageResponse>("subscription/usage"),
          api.get<PlanPriceResponse[]>("subscription/prices"),
          api.get<BillingSummaryResponse>("subscription/billing-summary"),
        ]);

        if (usageResult.status === "fulfilled") {
          setUsage(normalizeUsage(usageResult.value));
        } else {
          setUsage(null);
        }

        if (pricesResult.status === "fulfilled") {
          setPrices(pricesResult.value);
        } else {
          setPrices([]);
        }

        if (summaryResult.status === "rejected") {
          throw summaryResult.reason;
        }

        const summary = summaryResult.value;

        const summaryBillingProvider = normalizeBillingProvider(summary.billingProvider);
        const shouldLoadStripeData =
          isStripeBillingProvider(summaryBillingProvider) && hasStripeSubscriptionState(summary);
        if (!shouldLoadStripeData) {
          setBillingSummary(summary);
          setUpcomingInvoice(null);
          setInvoices([]);
          return summary;
        }

        const reconciledSeats = await api.post<SeatReconciliationResponse>("subscription/reconcile-seats");
        const reconciledSummary: BillingSummaryResponse = {
          ...summary,
          billableSeatQuantity: reconciledSeats.billableSeatQuantity ?? summary.billableSeatQuantity,
          currentPeriodStart: reconciledSeats.currentPeriodStart ?? summary.currentPeriodStart,
          currentPeriodEnd: reconciledSeats.currentPeriodEnd ?? summary.currentPeriodEnd,
        };

        const [upcomingResult, invoicesResult, paymentMethodResult] = await Promise.allSettled([
          api.get<UpcomingInvoiceResponse>("subscription/upcoming-invoice"),
          api.get<{ items?: InvoiceSummaryResponse[] | null }>("subscription/invoices", {
            query: { limit: "10" },
          }),
          api.get<PaymentMethodSummaryResponse>("subscription/payment-method"),
        ]);

        const summaryWithPaymentMethod =
          paymentMethodResult.status === "fulfilled"
            ? { ...reconciledSummary, defaultPaymentMethod: paymentMethodResult.value }
            : reconciledSummary;

        setBillingSummary(summaryWithPaymentMethod);

        setUpcomingInvoice(
          upcomingResult.status === "fulfilled" ? upcomingResult.value : null,
        );
        setInvoices(
          invoicesResult.status === "fulfilled"
            ? invoicesResult.value.items ?? []
            : [],
        );
        return summaryWithPaymentMethod;
      } catch (err) {
        console.error("Billing details failed:", err);
        setBillingError(true);
        return null;
      } finally {
        setBillingLoading(false);
      }
    },
    [api, ownerUser],
  );

  const refreshBillingState = useCallback(async (): Promise<BillingSummaryResponse | null> => {
    const [, summary] = await Promise.all([
      refreshTier(),
      loadBillingDetails({ silent: true }),
    ]);
    return summary;
  }, [loadBillingDetails, refreshTier]);

  const refreshBillingStateUntilPaymentMethod = useCallback(
    async (): Promise<BillingSummaryResponse | null> => {
      let latestSummary: BillingSummaryResponse | null = null;
      for (let attempt = 0; attempt < CHECKOUT_PAYMENT_METHOD_POLL_ATTEMPTS; attempt += 1) {
        if (attempt > 0) await delay(CHECKOUT_POLL_INTERVAL_MS);
        latestSummary = await refreshBillingState();
        if (latestSummary?.defaultPaymentMethod?.hasPaymentMethod) return latestSummary;
        if (
          latestSummary &&
          !(
            isStripeBillingProvider(normalizeBillingProvider(latestSummary.billingProvider)) &&
            hasStripeSubscriptionState(latestSummary)
          )
        ) {
          return latestSummary;
        }
      }
      return latestSummary;
    },
    [refreshBillingState],
  );

  useEffect(() => {
    void loadBillingDetails();
  }, [loadBillingDetails]);

  useEffect(() => {
    const handleBillingStateChanged = () => {
      void refreshBillingState();
    };
    window.addEventListener("landagility:billing-state-changed", handleBillingStateChanged);
    return () => window.removeEventListener("landagility:billing-state-changed", handleBillingStateChanged);
  }, [refreshBillingState]);

  const startCheckout = useCallback(
    async (targetTier: CheckoutTier) => {
      if (!api || actionLoading) return;
      if (!ownerUser) {
        setToast({ message: "Only the account Owner can change plans.", type: "error" });
        return;
      }
      setActionLoading(targetTier);
      try {
        const body: CheckoutRequest = {
          tier: targetTier,
          returnUrl: `${window.location.origin}/settings/billing?checkout=complete&tier=${targetTier}&session_id={CHECKOUT_SESSION_ID}`,
        };
        const result = await api.post<CheckoutResponse>(
          "subscription/checkout",
          body,
          { headers: { "Idempotency-Key": makeIdempotencyKey("checkout", targetTier) } },
        );
        if (!result?.clientSecret || !result?.publishableKey) {
          setToast({ message: "Checkout is not available yet. Please try again later.", type: "error" });
          return;
        }
        setCheckoutSession({
          tier: targetTier,
          clientSecret: result.clientSecret,
          publishableKey: result.publishableKey,
          seatQuantity: result.seatQuantity ?? 0,
        });
        window.setTimeout(() => {
          scrollElementIntoView("embeddedCheckout", { behavior: "smooth", block: "start" });
        }, 0);
      } catch (err) {
        console.error("Checkout start failed:", err);
        if (isApiError(err) && err.code === "ACTIVE_SUBSCRIPTION_EXISTS") {
          setToast({ message: "This tenant already has a paid subscription. Use Upgrade instead.", type: "error" });
        } else {
          setToast({ message: "Failed to start checkout.", type: "error" });
        }
      } finally {
        setActionLoading(null);
      }
    },
    [api, actionLoading, ownerUser],
  );

  const syncCheckoutReturn = useCallback(
    async (sessionId: string | null, targetTier: CheckoutTier | null) => {
      if (!api) return;
      setActionLoading("checkout-sync");
      try {
        if (sessionId) {
          const body: CheckoutReturnRequest = { sessionId };
          await api.post<CheckoutReturnResponse>("subscription/checkout/return", body);
          const summary = await refreshBillingStateUntilPaymentMethod();
          const paymentMethodReady = Boolean(summary?.defaultPaymentMethod?.hasPaymentMethod);
          setToast({ message: "Subscription updated.", type: "success" });
          if (!paymentMethodReady && summary && hasStripeSubscriptionState(summary)) {
            setToast({
              message: "Subscription updated. Payment method details are still syncing and should appear shortly.",
              type: "success",
            });
          }
          clearCheckoutSearch();
          return;
        }

        for (let attempt = 0; attempt < CHECKOUT_POLL_ATTEMPTS; attempt += 1) {
          if (attempt > 0) await delay(CHECKOUT_POLL_INTERVAL_MS);
          const subscription = await api.get<SubscriptionResponse>("subscription");
          await refreshBillingState();
          const status = subscription.status;
          const tierSynced = !targetTier || subscription.tier === targetTier;
          if (tierSynced && (status === "active" || status === "trialing")) {
            setToast({ message: "Subscription updated.", type: "success" });
            clearCheckoutSearch();
            return;
          }
        }

        setToast({
          message:
            "Checkout completed. Your subscription is still syncing and should update shortly.",
          type: "success",
        });
        clearCheckoutSearch();
      } catch (err) {
        console.error("Checkout sync failed:", err);
        setToast({
          message: "Checkout completed, but subscription sync could not be confirmed.",
          type: "error",
        });
      } finally {
        setActionLoading(null);
      }
    },
    [api, clearCheckoutSearch, refreshBillingState, refreshBillingStateUntilPaymentMethod],
  );

  const upgradePlan = useCallback(
    async (targetTier: PaidTier) => {
      if (!api || actionLoading) return;
      setActionLoading(targetTier);
      try {
        const body: UpgradeRequest = { targetTier };
        const result = await api.post<UpgradeResponse>(
          "subscription/upgrade",
          body,
          { headers: { "Idempotency-Key": makeIdempotencyKey("upgrade", targetTier) } },
        );
        await confirmStripePaymentIfNeeded(result);
        setLatestDowngradeImpact(null);
        await refreshBillingState();
        setToast({ message: `Plan changed to ${TIER_LABELS[targetTier]}.`, type: "success" });
      } catch (err) {
        console.error("Plan upgrade failed:", err);
        setToast({ message: errorMessage(err, "Failed to upgrade plan."), type: "error" });
      } finally {
        setActionLoading(null);
      }
    },
    [api, actionLoading, refreshBillingState],
  );

  const executePlanChange = useCallback(
    async (targetTier: TierLevel) => {
      if (!ownerUser) {
        setToast({ message: "Only the account Owner can change plans.", type: "error" });
        return;
      }

      const targetIdx = TIER_ORDER.indexOf(targetTier);
      if (targetTier === "enterprise" || !selfServicePlanChanges) return;
      if (needsPlanActivation) {
        if (targetTier === "free_crew") {
          setPlanDialog({ type: "cancel", targetTier });
          return;
        }
        if (isCheckoutTier(targetTier)) {
          await startCheckout(targetTier);
          return;
        }
        return;
      }

      if (targetIdx === currentTierIdx) return;

      if (targetIdx > currentTierIdx) {
        if (!isCheckoutTier(targetTier)) return;
        if (paidAndStripeBacked) {
          await upgradePlan(targetTier);
        } else {
          await startCheckout(targetTier);
        }
        return;
      }

      setPlanDialog({
        type: targetTier === "free_crew" ? "cancel" : "downgrade",
        targetTier,
      });
    },
    [currentTierIdx, needsPlanActivation, ownerUser, paidAndStripeBacked, selfServicePlanChanges, startCheckout, upgradePlan],
  );

  const requestPlanChange = useCallback(
    (targetTier: TierLevel) => {
      if (targetTier === "enterprise") {
        setEnterprisePromptTier(targetTier);
        return;
      }
      void executePlanChange(targetTier);
    },
    [executePlanChange],
  );

  const activateCurrentPlan = useCallback(() => {
    if (currentTier === "enterprise") {
      setEnterprisePromptTier(currentTier);
      return;
    }
    if (!isCheckoutTier(currentTier)) return;
    void startCheckout(currentTier);
  }, [currentTier, startCheckout]);

  const confirmEnterprisePlanChange = useCallback(async () => {
    if (!api || !enterprisePromptTier || enterpriseSubmitting) return;
    setEnterpriseSubmitting(true);
    try {
      const body: EnterpriseAcknowledgementRequest = {
        targetTier: enterprisePromptTier,
        acknowledgedSalesFollowUp: true,
        acknowledgedAdditionalIntegrationCosts: true,
      };
      await api.post("subscription/enterprise-acknowledgement", body);
      setEnterprisePromptTier(null);
      setToast({ message: "Enterprise request recorded. Sales will follow up.", type: "success" });
    } catch (err) {
      console.error("Enterprise acknowledgement failed:", err);
      setToast({ message: "Failed to record Enterprise acknowledgement.", type: "error" });
    } finally {
      setEnterpriseSubmitting(false);
    }
  }, [api, enterprisePromptTier, enterpriseSubmitting]);

  const confirmPlanDialog = useCallback(async () => {
    if (!api || !planDialog || actionLoading) return;
    setActionLoading(planDialog.type);
    try {
      if (planDialog.type === "cancel") {
        const body: CancelSubscriptionRequest = {
          effective: needsPlanActivation ? "immediate" : "period_end",
          reason: "tenant_requested",
        };
        await api.post(
          "subscription/cancel",
          body,
          { headers: { "Idempotency-Key": makeIdempotencyKey("cancel", currentTier) } },
        );
        setToast({
          message: needsPlanActivation ? "Switched to FreeCrew." : "Subscription cancellation scheduled.",
          type: "success",
        });
      } else {
        const body: DowngradeRequest = {
          targetTier: planDialog.targetTier,
          effective: "period_end",
        };
        const response = await api.post<DowngradeResponse>(
          "subscription/downgrade",
          body,
          { headers: { "Idempotency-Key": makeIdempotencyKey("downgrade", planDialog.targetTier) } },
        );
        setLatestDowngradeImpact(response);
        setToast({ message: `Downgrade to ${TIER_LABELS[planDialog.targetTier]} scheduled.`, type: "success" });
      }
      setPlanDialog(null);
      await refreshBillingState();
    } catch (err) {
      console.error("Plan change failed:", err);
      setToast({ message: errorMessage(err, "Failed to update subscription."), type: "error" });
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, api, currentTier, needsPlanActivation, planDialog, refreshBillingState]);

  const removeScheduledChange = useCallback(async () => {
    if (!api || actionLoading) return;
    setActionLoading("scheduled-change");
    try {
      await api.delete<ScheduledChangeRemovalResponse>("subscription/scheduled-change");
      setLatestDowngradeImpact(null);
      await refreshBillingState();
      setToast({ message: "Scheduled plan change removed.", type: "success" });
    } catch (err) {
      console.error("Scheduled change removal failed:", err);
      setToast({ message: errorMessage(err, "Failed to remove scheduled change."), type: "error" });
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, api, refreshBillingState]);

  const reactivateSubscription = useCallback(async () => {
    if (!api || actionLoading) return;
    setActionLoading("reactivate");
    try {
      await api.post("subscription/reactivate");
      await refreshBillingState();
      setToast({ message: "Subscription reactivated.", type: "success" });
    } catch (err) {
      console.error("Subscription reactivation failed:", err);
      if (isApiError(err) && err.code === "SUBSCRIPTION_TERMINATED" && isCheckoutTier(currentTier)) {
        await startCheckout(currentTier);
      } else {
        setToast({ message: errorMessage(err, "Failed to reactivate subscription."), type: "error" });
      }
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, api, currentTier, refreshBillingState, startCheckout]);

  const startPaymentMethodUpdate = useCallback(async (options?: PaymentSetupStartOptions) => {
    if (!api || actionLoading) return;
    setActionLoading("payment-method");
    if (options?.staleClientSecret) {
      setPaymentSetup(null);
    }
    try {
      const response = await api.post<SetupIntentResponse>("subscription/payment-method/setup-intent");
      if (!response.clientSecret || !response.publishableKey) {
        setToast({ message: "Payment method setup is not available yet.", type: "error" });
        return;
      }
      if (options?.staleClientSecret && response.clientSecret === options.staleClientSecret) {
        setToast({
          message: "Payment setup session is stale. Please try again after a fresh setup session is created.",
          type: "error",
        });
        return;
      }
      setPaymentSetup({
        clientSecret: response.clientSecret,
        publishableKey: response.publishableKey,
      });
      if (options?.staleClientSecret) {
        setToast({
          message: "Payment setup session refreshed. Please re-enter the card details.",
          type: "success",
        });
      }
    } catch (err) {
      console.error("Payment method setup failed:", err);
      setToast({ message: errorMessage(err, "Failed to start payment method setup."), type: "error" });
    } finally {
      setActionLoading(null);
    }
  }, [actionLoading, api]);

  const submitPaymentRetry = useCallback(async () => {
    if (!api) return;
    const response = await api.post<PaymentRetryResponse>(
      "subscription/payment/retry",
      undefined,
      { headers: { "Idempotency-Key": makeIdempotencyKey("payment-retry") } },
    );
    await confirmStripePaymentIfNeeded(response);
  }, [api]);

  const savePaymentMethod = useCallback(
    async (setupIntentId: string) => {
      if (!api) return;
      const shouldRetryPayment = paymentNeedsAttention && paidAndStripeBacked;
      const body: SetDefaultPaymentMethodRequest = { setupIntentId };
      let paymentMethod: PaymentMethodSummaryResponse;
      try {
        paymentMethod = await api.post<PaymentMethodSummaryResponse>(
          "subscription/payment-method/default",
          body,
          { headers: { "Idempotency-Key": makeIdempotencyKey("payment-method") } },
        );
      } catch (err) {
        if (isApiError(err) && err.code === "PAYMENT_RECOVERY_FAILED") {
          setPaymentSetup(null);
          await refreshBillingState();
          setToast({
            message: paymentMethodErrorMessage(err, "Payment method updated, but we couldn't retry the overdue payment."),
            type: "error",
          });
          return;
        }
        throw err;
      }
      setBillingSummary((prev) =>
        prev ? { ...prev, defaultPaymentMethod: paymentMethod } : prev,
      );
      setPaymentSetup(null);

      if (shouldRetryPayment) {
        try {
          await submitPaymentRetry();
          await refreshBillingState();
          setToast({ message: "Payment method updated and payment retry submitted.", type: "success" });
        } catch (err) {
          console.error("Payment retry after payment method update failed:", err);
          await refreshBillingState();
          setToast({
            message: paymentMethodErrorMessage(err, "Payment method updated, but we couldn't retry the overdue payment."),
            type: "error",
          });
        }
        return;
      }

      await refreshBillingState();
      setToast({ message: "Payment method updated.", type: "success" });
    },
    [api, paidAndStripeBacked, paymentNeedsAttention, refreshBillingState, submitPaymentRetry],
  );

  const retryPayment = useCallback(
    async () => {
      if (!api || actionLoading) return;
      setActionLoading("payment-retry");
      try {
        await submitPaymentRetry();
        await refreshBillingState();
        setToast({ message: "Payment retry submitted.", type: "success" });
      } catch (err) {
        console.error("Payment retry failed:", err);
        setToast({ message: errorMessage(err, "Failed to retry payment."), type: "error" });
      } finally {
        setActionLoading(null);
      }
    },
    [actionLoading, api, refreshBillingState, submitPaymentRetry],
  );

  const embeddedCheckoutOptions = useMemo(
    () =>
      checkoutSession
        ? {
            clientSecret: checkoutSession.clientSecret,
            onComplete: () => {
              const completedTier = checkoutSession.tier;
              setCheckoutSession(null);
              void syncCheckoutReturn(null, completedTier);
            },
          }
        : undefined,
    [checkoutSession, syncCheckoutReturn],
  );

  useEffect(() => {
    if (!requestedTier) return;
    window.setTimeout(() => {
      scrollElementIntoView(`planColumn-${requestedTier}`, {
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }, 0);
  }, [requestedTier]);

  useEffect(() => {
    if (
      checkoutMode !== "start" ||
      !requestedTier ||
      !api ||
      loading ||
      billingLoading ||
      checkoutSession ||
      actionLoading
    ) {
      return;
    }

    const autoStartKey = `${checkoutMode}:${requestedTier}:${currentTier}`;
    if (autoStartKeyRef.current === autoStartKey) return;
    autoStartKeyRef.current = autoStartKey;

    requestPlanChange(requestedTier);
  }, [
    actionLoading,
    api,
    billingLoading,
    checkoutMode,
    checkoutSession,
    currentTier,
    loading,
    requestPlanChange,
    requestedTier,
  ]);

  useEffect(() => {
    if (checkoutMode !== "complete" || !api || loading || billingLoading) return;
    const completeKey = searchParams.toString();
    if (checkoutCompleteKeyRef.current === completeKey) return;
    checkoutCompleteKeyRef.current = completeKey;
    void syncCheckoutReturn(checkoutSessionId, requestedTier);
  }, [
    api,
    billingLoading,
    checkoutMode,
    checkoutSessionId,
    loading,
    requestedTier,
    searchParams,
    syncCheckoutReturn,
  ]);

  const priceMap = useMemo(() => {
    const entries = prices
      .filter((p): p is PlanPriceResponse & { tier: TierLevel } => isTierLevel(p.tier))
      .map((p) => [p.tier, p] as const);
    return Object.fromEntries(entries) as Record<TierLevel, PlanPriceResponse | undefined>;
  }, [prices]);

  const renderCellValue = (val: boolean | string) => {
    if (val === true) {
      return (
        <span className={styles.check}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8l3.5 3.5L13 5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      );
    }
    if (val === false) {
      return (
        <span className={styles.cross}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 8h8" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      );
    }
    return <span>{val}</span>;
  };

  const loadingPage = loading || billingLoading;
  const scheduledChange = billingSummary?.scheduledChange;
  const downgradeImpactFeatures = latestDowngradeImpact?.featuresLosingAccess ?? [];
  const scheduledFromTier = scheduledChange
    ? normalizeTier(scheduledChange.fromTier) ?? currentTier
    : currentTier;
  const scheduledToTier = scheduledChange
    ? normalizeTier(scheduledChange.toTier) ?? currentTier
    : currentTier;
  const scheduledFromTierLabel = TIER_LABELS[scheduledFromTier];
  const scheduledToTierLabel = TIER_LABELS[scheduledToTier];
  const scheduledTargetTier = scheduledChange ? scheduledToTier : null;
  const scheduledChangeActionLabel =
    scheduledChange?.type === "downgrade"
      ? `Keep ${scheduledFromTierLabel}`
      : "Remove scheduled change";
  const scheduledChangeDate = formatDate(scheduledChange?.effectiveAt);
  const scheduledPlanPrice = priceMap[scheduledToTier];
  const scheduledEstimatedMonthlyTotal =
    scheduledChange && scheduledPlanPrice && !scheduledPlanPrice.isContactSales &&
    hasMoneyAmount(scheduledPlanPrice.baseMonthlyPrice) &&
    hasMoneyAmount(scheduledPlanPrice.seatMonthlyPrice) &&
    billingSummary?.billableSeatQuantity !== null &&
    billingSummary?.billableSeatQuantity !== undefined
      ? scheduledPlanPrice.baseMonthlyPrice + (billingSummary.billableSeatQuantity * scheduledPlanPrice.seatMonthlyPrice)
      : null;
  const billableUserLimit = usage?.users.max;
  const clientLimit = usage?.clients.max;
  const currentBaseMonthlyPrice = paidAndStripeBacked && !externalBilling ? billingSummary?.baseMonthlyPrice : null;
  const currentSeatMonthlyPrice = paidAndStripeBacked && !externalBilling ? billingSummary?.seatMonthlyPrice : null;
  const estimatedMonthlyTotal = paidAndStripeBacked && !externalBilling ? billingSummary?.estimatedMonthlyTotal : null;
  const hasDefaultPaymentMethod = Boolean(billingSummary?.defaultPaymentMethod?.hasPaymentMethod);
  const planStatus = getPlanStatusPresentation({
    status: currentStatus,
    paymentHealth: billingSummary?.paymentHealth,
    cancelAtPeriodEnd,
    needsPlanActivation,
    tierLabel: TIER_LABELS[currentTier] ?? currentTier,
    periodEnd: summaryPeriodEnd,
    gracePeriodEnd: summaryGracePeriodEnd,
  });
  const displayedPlanStatus: PlanStatusPresentation = externalBilling
    ? {
        tone: "neutral",
        badgeLabel: null,
        headerDetail: null,
        headline: "",
        body: "",
        showBanner: false,
      }
    : planStatus;
  const healthyPaidPlan =
    Boolean(billingSummary) &&
    currentTier !== "free_crew" &&
    paidAndStripeBacked &&
    currentStatus === "active" &&
    displayedPlanStatus.tone === "neutral" &&
    displayedPlanStatus.badgeLabel === null;
  const currentPlanActionNote = needsPlanActivation
    ? `Your tenant has ${TIER_LABELS[currentTier]} access, but billing is not active in Stripe.`
    : externalBilling
      ? "Your subscription is managed by LandAgility. Contact support to make changes."
      : customStripeBilling
        ? "This tenant uses custom Stripe billing. Contact sales for pricing or plan changes."
        : unknownBillingProvider
          ? "Billing management is unavailable until the billing provider is confirmed."
          : paymentNeedsAttention && paidAndStripeBacked
            ? "Resolve the payment issue to keep paid features active."
            : currentStatus === "incomplete" || currentStatus === "incomplete_expired"
              ? "Finish checkout to activate billing for this plan."
              : "Upgrades take effect immediately. Downgrades and cancellations take effect at the end of the current period.";

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <div className={styles["page-shell"]}>
        {(error || billingError) && (
          <div className={styles["error-banner"]}>
            Unable to load subscription details. Please try again later.
          </div>
        )}

        {!ownerUser && (
          <div className={styles["billing-alert"]}>
            Billing can only be managed by the account Owner.
          </div>
        )}

        {loadingPage ? (
          <div className={styles["bill-card"]}>
            <div className={styles["bill-card-header"]}>
              <span className={styles["bill-card-header-title"]}>Loading...</span>
            </div>
            <div className={styles["bill-card-body"]}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "12px 0", opacity: 0.5 }}>
                  <span className={styles["bill-skeleton"]} style={{ width: 80, height: 13 }} />
                  <span className={styles["bill-skeleton"]} style={{ width: 140, height: 13 }} />
                  <span className={styles["bill-skeleton"]} style={{ width: 60, height: 13 }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={styles["bill-card"]}>
              <div className={`${styles["bill-card-header"]} ${styles["current-plan-header"]}`}>
                <span className={styles["bill-card-header-title"]}>Current Plan</span>
                <div className={styles["current-plan-meta"]}>
                  <span
                    className={`${styles["plan-badge"]} ${healthyPaidPlan ? styles["plan-badge--healthy"] : ""}`}
                    title={`Current plan: ${TIER_LABELS[currentTier] ?? currentTier}`}
                  >
                    {TIER_LABELS[currentTier] ?? currentTier}
                  </span>
                  {displayedPlanStatus.badgeLabel && (
                    <span
                      className={`${styles["status-badge"]} ${statusToneClass(displayedPlanStatus.tone)}`}
                      title={`Subscription status: ${statusLabel(currentStatus)}`}
                    >
                      <span className={styles["status-dot"]} />
                      {displayedPlanStatus.badgeLabel}
                    </span>
                  )}
                  {displayedPlanStatus.headerDetail && (
                    <span className={styles["plan-period"]}>
                      {scheduledChange?.type === "downgrade" && summaryPeriodEnd
                        ? `Switches ${scheduledChangeDate}`
                        : displayedPlanStatus.headerDetail}
                    </span>
                  )}
                  {actionLoading === "checkout-sync" && (
                    <span className={styles["plan-period"]}>Syncing checkout...</span>
                  )}
                </div>
              </div>
              <div className={styles["bill-card-body"]}>
                {displayedPlanStatus.showBanner && (
                  <div
                    className={`${styles["plan-status-banner"]} ${styles[`plan-status-banner--${displayedPlanStatus.tone}`]}`}
                    role={displayedPlanStatus.tone === "danger" ? "alert" : "status"}
                  >
                    <span className={styles["plan-status-banner-icon"]} aria-hidden="true">
                      {displayedPlanStatus.tone === "danger" ? "!" : displayedPlanStatus.tone === "warning" ? "!" : "i"}
                    </span>
                    <div className={styles["plan-status-banner-copy"]}>
                      <div className={styles["plan-status-banner-title"]}>{displayedPlanStatus.headline}</div>
                      <div className={styles["plan-status-banner-body"]}>{displayedPlanStatus.body}</div>
                    </div>
                    <div className={styles["plan-status-banner-actions"]}>
                      {selfServicePlanChanges && needsPlanActivation && isCheckoutTier(currentTier) && (
                        <button
                          type="button"
                          className={styles["primary-btn"]}
                          onClick={activateCurrentPlan}
                          disabled={actionLoading !== null || !ownerUser}
                        >
                          {actionLoading === currentTier ? "Starting..." : `Activate ${TIER_LABELS[currentTier]}`}
                        </button>
                      )}
                      {paymentNeedsAttention && paidAndStripeBacked && (
                        <>
                          <button
                            type="button"
                            className={styles["primary-btn"]}
                            onClick={() => void startPaymentMethodUpdate()}
                            disabled={actionLoading !== null}
                          >
                            {actionLoading === "payment-method" ? "Starting..." : "Update payment method"}
                          </button>
                          <button
                            type="button"
                            className={styles["secondary-btn"]}
                            onClick={() => void retryPayment()}
                            disabled={actionLoading !== null}
                          >
                            {actionLoading === "payment-retry" ? "Retrying..." : "Retry payment"}
                          </button>
                        </>
                      )}
                      {currentStatus === "trialing" && paidAndStripeBacked && (
                        <>
                          {!hasDefaultPaymentMethod && (
                            <button
                              type="button"
                              className={styles["primary-btn"]}
                              onClick={() => void startPaymentMethodUpdate()}
                              disabled={actionLoading !== null}
                            >
                              {actionLoading === "payment-method" ? "Starting..." : "Add payment method"}
                            </button>
                          )}
                          {selfServicePlanChanges && (
                            <button
                              type="button"
                              className={hasDefaultPaymentMethod ? styles["primary-btn"] : styles["secondary-btn"]}
                              onClick={() => scrollElementIntoView("planComparison", { behavior: "smooth" })}
                              disabled={actionLoading !== null}
                            >
                              Change Plan
                            </button>
                          )}
                        </>
                      )}
                      {(currentStatus === "incomplete" || currentStatus === "incomplete_expired") &&
                        selfServicePlanChanges && isCheckoutTier(currentTier) && (
                          <button
                            type="button"
                            className={styles["primary-btn"]}
                            onClick={activateCurrentPlan}
                            disabled={actionLoading !== null || !ownerUser}
                          >
                            {actionLoading === currentTier ? "Starting..." : "Start checkout"}
                          </button>
                        )}
                      {selfServicePlanChanges && cancelAtPeriodEnd && (
                        <button
                          type="button"
                          className={styles["primary-btn"]}
                          onClick={() => void reactivateSubscription()}
                          disabled={actionLoading !== null}
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!externalBilling && (summaryPeriodStart || summaryPeriodEnd) && (
                  <div className={styles["current-plan-period-note"]}>
                    Billing period: {formatDate(summaryPeriodStart)} - {formatDate(summaryPeriodEnd)}
                  </div>
                )}

                <div className={`${styles["summary-grid"]} ${styles["current-summary-grid"]}`}>
                  {usage && (
                    <div className={styles["summary-item"]}>
                      <span className={styles["summary-label"]}>{usageKpiLabel(usage.users, currentTier, "users")}</span>
                      <span className={`${styles["summary-value"]} ${
                        usage.users.max !== null && usage.users.current >= usage.users.max
                          ? styles["usage-at-limit"] : ""
                      }`}>
                        {usageKpiValue(
                          usage.users,
                          currentTier,
                          !externalBilling ? billingSummary?.billableSeatQuantity : null,
                        )}
                      </span>
                      <span className={styles["summary-limit"]}>{userLimitLabel(billableUserLimit, currentTier)}</span>
                      {usageBasisFor(usage.users, currentTier) === "active" && (
                        <span className={styles["summary-detail"]}>Active &ge;1 day this period</span>
                      )}
                    </div>
                  )}
                  {usage && (
                    <div className={styles["summary-item"]}>
                      <span className={styles["summary-label"]}>{usageKpiLabel(usage.clients, currentTier, "clients")}</span>
                      <span className={`${styles["summary-value"]} ${
                        usage.clients.max !== null && usage.clients.current >= usage.clients.max
                          ? styles["usage-at-limit"] : ""
                      }`}>{usageKpiValue(usage.clients, currentTier)}</span>
                      <span className={styles["summary-limit"]}>{tierLimitLabel(clientLimit, currentTier, "clients")}</span>
                    </div>
                  )}
                  {!externalBilling && (
                    <>
                      <div className={styles["summary-item"]}>
                        <span className={styles["summary-label"]}>Base monthly price</span>
                        <span
                          className={`${styles["summary-value"]} ${
                            hasMoneyAmount(currentBaseMonthlyPrice) ? "" : styles["summary-placeholder"]
                          }`}
                        >
                          {hasMoneyAmount(currentBaseMonthlyPrice)
                            ? formatMoney(currentBaseMonthlyPrice, billingSummary?.currency)
                            : "--"}
                        </span>
                        <span className={styles["summary-help"]}>
                          {customStripeBilling ? "Custom base charge" : "Plan base charge"}
                        </span>
                      </div>
                      <div className={styles["summary-item"]}>
                        <span className={styles["summary-label"]}>Per-user price</span>
                        <span
                          className={`${styles["summary-value"]} ${
                            hasMoneyAmount(currentSeatMonthlyPrice) ? "" : styles["summary-placeholder"]
                          }`}
                        >
                          {hasMoneyAmount(currentSeatMonthlyPrice)
                            ? formatMoney(currentSeatMonthlyPrice, billingSummary?.currency)
                            : "--"}
                        </span>
                        {!hasMoneyAmount(currentSeatMonthlyPrice) && (
                          <span className={styles["summary-help"]}>Set at billing close</span>
                        )}
                      </div>
                      <div className={styles["summary-item"]}>
                        <span className={styles["summary-label"]}>Estimated monthly total</span>
                        <span
                          className={`${styles["summary-value"]} ${
                            hasMoneyAmount(estimatedMonthlyTotal) ? "" : styles["summary-placeholder"]
                          }`}
                        >
                          {hasMoneyAmount(estimatedMonthlyTotal)
                            ? formatMoney(estimatedMonthlyTotal, billingSummary?.currency)
                            : "--"}
                        </span>
                        {!hasMoneyAmount(estimatedMonthlyTotal) && (
                          <span className={styles["summary-help"]}>
                            {customStripeBilling ? "Custom Stripe billing" : "Base + users x price"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {(!displayedPlanStatus.showBanner || (paidAndStripeBacked && !cancelAtPeriodEnd)) && (
                  <div className={styles["current-plan-actions"]}>
                    <div className={styles["current-plan-action-note"]}>
                      {currentPlanActionNote}
                    </div>
                    <div className={styles["plan-actions"]}>
                      {!displayedPlanStatus.showBanner && selfServicePlanChanges && currentTier !== "enterprise" && !scheduledChange && (
                        <button
                          type="button"
                          className={styles["primary-btn"]}
                          onClick={() => scrollElementIntoView("planComparison", { behavior: "smooth" })}
                        >
                          Change Plan
                        </button>
                      )}
                      {selfServicePlanChanges && paidAndStripeBacked && !cancelAtPeriodEnd && (
                        <button
                          type="button"
                          className={styles["danger-btn"]}
                          aria-label="Cancel subscription and switch to FreeCrew at period end"
                          onClick={() => void executePlanChange("free_crew")}
                          disabled={actionLoading !== null || !ownerUser}
                        >
                          Cancel Subscription
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {!externalBilling && scheduledChange && (
                  <div className={styles["scheduled-change"]} role="status">
                    <div className={styles["scheduled-change-main"]}>
                      <span className={styles["scheduled-change-icon"]} aria-hidden="true">
                       <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                         <path
                           d="M5.25 2.25v3M12.75 2.25v3M3 7.5h12M4.5 3.75h9A1.5 1.5 0 0 1 15 5.25v8.25A1.5 1.5 0 0 1 13.5 15h-9A1.5 1.5 0 0 1 3 13.5V5.25a1.5 1.5 0 0 1 1.5-1.5Z"
                           stroke="currentColor"
                           strokeWidth="1.5"
                           strokeLinecap="round"
                           strokeLinejoin="round"
                         />
                       </svg>
                     </span>
                     <div className={styles["scheduled-change-copy"]}>
                       <div className={styles["scheduled-change-title-row"]}>
                         <span className={styles["scheduled-change-chip"]}>Scheduled</span>
                         <div className={styles["scheduled-change-title"]}>
                           {scheduledChange.type === "downgrade"
                             ? `Switch to ${scheduledToTierLabel} on ${scheduledChangeDate}`
                             : `Change to ${scheduledToTierLabel} on ${scheduledChangeDate}`}
                         </div>
                       </div>
                       <div className={styles["scheduled-change-detail"]}>
                         {scheduledChange.type === "downgrade"
                           ? `Your ${scheduledFromTierLabel} access continues until then.`
                           : `Your plan changes from ${scheduledFromTierLabel} to ${scheduledToTierLabel} then.`}
                       </div>
                       {scheduledEstimatedMonthlyTotal !== null && (
                         <div className={styles["scheduled-change-detail"]}>
                           Starting {scheduledChangeDate}, estimated monthly total:{" "}
                           <strong>{formatMoney(scheduledEstimatedMonthlyTotal, scheduledPlanPrice?.currency)}/mo</strong>.
                         </div>
                       )}
                     </div>
                   </div>
                   {downgradeImpactFeatures.length > 0 && (
                     <div className={styles["impact-list"]}>
                       <span className={styles["impact-title"]}>Not included in {scheduledToTierLabel}:</span>
                       {downgradeImpactFeatures.map((feature) => (
                         <span key={feature.key ?? feature.displayName} className={styles["impact-pill"]}>
                           {feature.displayName ?? feature.key}
                         </span>
                       ))}
                     </div>
                   )}
                   <div className={styles["scheduled-change-actions"]}>
                     {selfServicePlanChanges && currentTier !== "enterprise" && (
                       <button
                         type="button"
                         className={styles["scheduled-change-link"]}
                         onClick={() => scrollElementIntoView("planComparison", { behavior: "smooth" })}
                       >
                         Change scheduled plan
                       </button>
                     )}
                     {selfServicePlanChanges && scheduledChange.canCancel && (
                       <button
                         type="button"
                         className={`${styles["secondary-btn"]} ${styles["scheduled-change-action"]}`}
                         aria-label={`Remove scheduled change and ${scheduledChangeActionLabel.toLowerCase()}`}
                         onClick={() => void removeScheduledChange()}
                         disabled={actionLoading !== null}
                       >
                         {scheduledChangeActionLabel}
                       </button>
                     )}
                   </div>
                  </div>
                )}
              </div>
            </div>

            {checkoutSession && stripePromise && embeddedCheckoutOptions && (
              <div
                className={styles["bill-card"]}
                id="embeddedCheckout"
                data-testid="embedded-checkout-card"
              >
                <div className={styles["bill-card-header"]}>
                  <span className={styles["bill-card-header-title"]}>
                    {TIER_LABELS[checkoutSession.tier]} Checkout
                  </span>
                  <button
                    type="button"
                    className={styles["checkout-close-btn"]}
                    onClick={() => setCheckoutSession(null)}
                  >
                    Close checkout
                  </button>
                </div>
                <div className={styles["bill-card-body"]}>
                  <div className={styles["checkout-intro"]}>
                    Complete secure payment details below. LandAgility will update your plan as soon as Stripe confirms the subscription.
                    {checkoutSession.seatQuantity > 0 && (
                      <span className={styles["checkout-seat-note"]}>
                        Seat quantity: {checkoutSession.seatQuantity}
                      </span>
                    )}
                  </div>
                  <div className={styles["embedded-checkout-shell"]}>
                    <EmbeddedCheckoutProvider
                      stripe={stripePromise}
                      options={embeddedCheckoutOptions}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                </div>
              </div>
            )}

            {paidAndStripeBacked && (
              <>
                <div className={styles["payment-method-strip"]} aria-label="Payment method">
                  <div className={styles["billing-detail-row"]}>
                    <div className={styles["billing-detail-copy"]}>
                      <span className={styles["billing-detail-label"]}>
                        {billingSummary?.defaultPaymentMethod?.hasPaymentMethod ? "Paid with" : "Payment method"}
                      </span>
                      <span className={styles["billing-detail-value"]}>
                        {billingSummary?.defaultPaymentMethod?.hasPaymentMethod
                          ? paymentMethodLabel(billingSummary.defaultPaymentMethod)
                          : "No payment method on file"}
                      </span>
                      {billingSummary?.defaultPaymentMethod?.billingEmail && (
                        <span className={styles["billing-detail-help"]}>
                          {billingSummary.defaultPaymentMethod.billingEmail}
                        </span>
                      )}
                      {!billingSummary?.defaultPaymentMethod?.hasPaymentMethod && (
                        <span className={styles["billing-detail-help"]}>
                          Add a payment method to keep subscription payments current.
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles["billing-detail-action"]}
                      onClick={() => void startPaymentMethodUpdate()}
                      disabled={actionLoading !== null}
                    >
                      Update payment method
                    </button>
                  </div>
                </div>

                <div className={styles["bill-card"]}>
                  <div className={styles["bill-card-header"]}>
                    <span className={styles["bill-card-header-title"]}>Invoices</span>
                  </div>
                  <div className={styles["bill-card-body"]}>
                    <section className={styles["invoice-section"]} aria-labelledby="upcomingInvoiceHeading">
                      <div className={styles["invoice-section-header"]}>
                        <h3 id="upcomingInvoiceHeading" className={styles["invoice-section-title"]}>
                          Upcoming Invoice
                        </h3>
                      </div>
                      {upcomingInvoice ? (
                        <>
                          <div className={styles["invoice-summary-strip"]}>
                            <div className={styles["invoice-summary-item"]}>
                              <span className={styles["invoice-summary-label"]}>Period</span>
                              <span className={styles["invoice-summary-value"]}>
                                {formatDate(upcomingInvoice.periodStart)} - {formatDate(upcomingInvoice.periodEnd)}
                              </span>
                            </div>
                            <div className={styles["invoice-summary-item"]}>
                              <span className={styles["invoice-summary-label"]}>Billable users</span>
                              <span className={styles["invoice-summary-value"]}>{upcomingInvoice.billableSeatQuantity ?? "--"}</span>
                            </div>
                            <div className={styles["invoice-summary-item"]}>
                              <span className={styles["invoice-summary-label"]}>Credits</span>
                              <span className={styles["invoice-summary-value"]}>
                                {formatMoney(upcomingInvoice.credits, upcomingInvoice.currency)}
                              </span>
                            </div>
                            <div className={`${styles["invoice-summary-item"]} ${styles["invoice-summary-total"]}`}>
                              <span className={styles["invoice-summary-label"]}>Total</span>
                              <span className={styles["invoice-summary-value"]}>
                                {formatMoney(upcomingInvoice.total, upcomingInvoice.currency)}
                              </span>
                            </div>
                          </div>
                          <div className={styles["invoice-table-shell"]}>
                            <table className={styles["billing-table"]}>
                              <thead>
                                <tr>
                                  <th>Description</th>
                                  <th className={styles["billing-table-number"]}>Qty</th>
                                  <th className={styles["billing-table-number"]}>Unit</th>
                                  <th className={styles["billing-table-number"]}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(upcomingInvoice.lineItems ?? []).map((item, index) => (
                                  <tr key={`${item.type ?? "line"}-${index}`}>
                                    <td>{item.description ?? paymentHealthLabel(item.type)}</td>
                                    <td className={styles["billing-table-number"]}>{item.quantity ?? "--"}</td>
                                    <td className={styles["billing-table-number"]}>{formatMoney(item.unitAmount, upcomingInvoice.currency)}</td>
                                    <td className={styles["billing-table-number"]}>{formatMoney(item.amount, upcomingInvoice.currency)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={3}>Subtotal</td>
                                  <td className={styles["billing-table-number"]}>{formatMoney(upcomingInvoice.subtotal, upcomingInvoice.currency)}</td>
                                </tr>
                                <tr>
                                  <td colSpan={3}>Credits</td>
                                  <td className={styles["billing-table-number"]}>{formatMoney(upcomingInvoice.credits, upcomingInvoice.currency)}</td>
                                </tr>
                                {hasMoneyAmount(upcomingInvoice.tax) && upcomingInvoice.tax !== 0 && (
                                  <tr>
                                    <td colSpan={3}>Tax</td>
                                    <td className={styles["billing-table-number"]}>{formatMoney(upcomingInvoice.tax, upcomingInvoice.currency)}</td>
                                  </tr>
                                )}
                                <tr className={styles["billing-table-total-row"]}>
                                  <td colSpan={3}>Total</td>
                                  <td className={styles["billing-table-number"]}>{formatMoney(upcomingInvoice.total, upcomingInvoice.currency)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className={`${styles["empty-state"]} ${styles["empty-state-compact"]}`}>
                          <span className={styles["empty-state-icon"]} aria-hidden="true">
                            <svg width="28" height="28" viewBox="0 0 20 20" fill="none">
                              <path d="M5 2.5h10v15l-2-1.25-2 1.25-2-1.25-2 1.25-2-1.25V2.5Z" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M7.5 7h5M7.5 10h5M7.5 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </span>
                          <span className={styles["empty-state-title"]}>No upcoming invoice yet</span>
                          <span className={styles["empty-state-copy"]}>
                            Invoice details will appear after Stripe prepares the next billing cycle.
                          </span>
                        </div>
                      )}
                    </section>

                    <section className={styles["invoice-section"]} aria-labelledby="invoiceHistoryHeading">
                      <div className={styles["invoice-section-header"]}>
                        <h3 id="invoiceHistoryHeading" className={styles["invoice-section-title"]}>
                          Invoice History
                        </h3>
                      </div>
                      {invoices.length > 0 ? (
                        <div className={styles["invoice-table-shell"]}>
                          <table className={styles["billing-table"]}>
                            <thead>
                              <tr>
                                <th>Invoice</th>
                                <th>Status</th>
                                <th>Period</th>
                                <th className={styles["billing-table-number"]}>Amount Due</th>
                                <th className={styles["billing-table-number"]}>Paid</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoices.map((invoice) => (
                                <tr key={invoice.id ?? invoice.number} data-testid={`invoice-row-${invoice.id ?? invoice.number}`}>
                                  <td>{invoice.number ?? invoice.id ?? "--"}</td>
                                  <td>{paymentHealthLabel(invoice.status)}</td>
                                  <td>
                                    {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                                  </td>
                                  <td className={styles["billing-table-number"]}>{formatMoney(invoice.amountDue, invoice.currency)}</td>
                                  <td className={styles["billing-table-number"]}>{formatMoney(invoice.amountPaid, invoice.currency)}</td>
                                  <td>
                                    <div className={styles["invoice-actions"]}>
                                      {invoice.invoicePdfUrl && (
                                        <a
                                          className={styles["table-link"]}
                                          href={invoice.invoicePdfUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          PDF
                                        </a>
                                      )}
                                      {invoice.canRetryPayment && (
                                        <button
                                          type="button"
                                          className={styles["inline-action-btn"]}
                                          onClick={() => void retryPayment()}
                                          disabled={actionLoading !== null}
                                        >
                                          Retry
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className={`${styles["empty-state"]} ${styles["empty-state-compact"]}`}>
                          <span className={styles["empty-state-icon"]} aria-hidden="true">
                            <svg width="28" height="28" viewBox="0 0 20 20" fill="none">
                              <path d="M5 2.5h10v15l-2-1.25-2 1.25-2-1.25-2 1.25-2-1.25V2.5Z" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M7.5 7h5M7.5 10h5M7.5 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </span>
                          <span className={styles["empty-state-title"]}>No invoices are available yet</span>
                          <span className={styles["empty-state-copy"]}>
                            Paid invoices will appear here once billing starts.
                          </span>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              </>
            )}

            {!externalBilling && (
              <div
                className={`${styles["bill-card"]} ${styles["plan-comparison-card"]}`}
                id="planComparison"
              >
                <div className={styles["bill-card-header"]}>
                  <span className={styles["bill-card-header-title"]}>Plan Comparison</span>
                </div>
                <div className={styles["bill-card-body-flush"]}>
                  <table className={styles["comparison-table"]}>
                  <thead>
                    <tr>
                      <th>Feature</th>
                      {TIER_ORDER.map((t, idx) => (
                        <th
                          key={t}
                          id={`planColumn-${t}`}
                          className={
                            idx === currentTierIdx
                              ? styles["current-col"]
                              : scheduledTargetTier === t
                                ? styles["scheduled-col"]
                                : requestedTier === t
                                  ? styles["requested-col"]
                                  : ""
                          }
                        >
                          <span className={styles["plan-header-name"]}>{TIER_LABELS[t]}</span>
                          {idx === currentTierIdx && (
                            <div className={styles["plan-column-note"]}>Current</div>
                          )}
                          {scheduledTargetTier === t && idx !== currentTierIdx && (
                            <div className={styles["plan-column-note"]}>Scheduled</div>
                          )}
                          {requestedTier === t && idx !== currentTierIdx && (
                            <div className={styles["plan-column-note"]}>Selected</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_FEATURES.map((feat) => (
                      <tr key={feat.key} data-testid={`plan-feature-${feat.key}`}>
                        <td>{feat.label}</td>
                        {(["free", "core", "pro", "enterprise"] as const).map((col, idx) => {
                          const colTier = TIER_ORDER[idx];
                          return (
                            <td
                              key={col}
                              className={
                                idx === currentTierIdx
                                  ? styles["current-col"]
                                  : scheduledTargetTier === colTier
                                    ? styles["scheduled-col"]
                                    : requestedTier === colTier
                                      ? styles["requested-col"]
                                      : ""
                              }
                            >
                              {renderCellValue(feat[col])}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {!externalBilling && (
                      <>
                        <tr className={styles["price-row"]}>
                          <td>Base price</td>
                          {TIER_ORDER.map((t, idx) => {
                            const p = priceMap[t];
                            return (
                              <td
                                key={t}
                                className={
                                  idx === currentTierIdx
                                    ? styles["current-col"]
                                    : scheduledTargetTier === t
                                      ? styles["scheduled-col"]
                                      : requestedTier === t
                                        ? styles["requested-col"]
                                        : ""
                                }
                              >
                                {customStripeBilling && t !== "free_crew"
                                  ? "Custom"
                                  : p?.isContactSales
                                    ? "Custom"
                                    : formatPlanPrice(planBaseMonthlyPrice(p), p?.currency, "/mo", "Free")}
                              </td>
                            );
                          })}
                        </tr>

                        <tr className={styles["price-row"]}>
                          <td>Per-user price</td>
                          {TIER_ORDER.map((t, idx) => {
                            const p = priceMap[t];
                            return (
                              <td
                                key={t}
                                className={
                                  idx === currentTierIdx
                                    ? styles["current-col"]
                                    : scheduledTargetTier === t
                                      ? styles["scheduled-col"]
                                      : requestedTier === t
                                        ? styles["requested-col"]
                                        : ""
                                }
                              >
                                {customStripeBilling && t !== "free_crew"
                                  ? "Custom"
                                  : p?.isContactSales
                                    ? "Custom"
                                    : formatPlanPrice(planSeatMonthlyPrice(p), p?.currency)}
                              </td>
                            );
                          })}
                        </tr>
                      </>
                    )}

                    <tr>
                      <td />
                      {TIER_ORDER.map((t, idx) => {
                        const isCurrent = idx === currentTierIdx;
                        const isUpgrade = idx > currentTierIdx;
                        const isEnterprise = t === "enterprise";
                        const isScheduledTarget = scheduledTargetTier === t && !isCurrent;
                        const isActivationTarget = selfServicePlanChanges && needsPlanActivation && isCheckoutTier(t);
                        return (
                          <td
                            key={t}
                            className={
                              isCurrent
                                ? styles["current-col"]
                                : isScheduledTarget
                                  ? styles["scheduled-col"]
                                  : requestedTier === t
                                    ? styles["requested-col"]
                                    : ""
                            }
                          >
                            {isCurrent && selfServicePlanChanges && scheduledChange?.canCancel ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                onClick={() => void removeScheduledChange()}
                                disabled={actionLoading !== null}
                                aria-label={`Remove scheduled change and ${scheduledChangeActionLabel.toLowerCase()}`}
                              >
                                {scheduledChangeActionLabel}
                              </button>
                            ) : isCurrent && isActivationTarget ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                onClick={() => requestPlanChange(t)}
                                disabled={actionLoading !== null}
                                aria-label={`Activate ${TIER_LABELS[t]}`}
                              >
                                {actionLoading === t ? "Starting..." : `Activate ${TIER_LABELS[t]}`}
                              </button>
                            ) : isCurrent ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                disabled
                              >
                                Current Plan
                              </button>
                            ) : isScheduledTarget ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                disabled
                              >
                                Scheduled
                              </button>
                            ) : isActivationTarget ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                onClick={() => requestPlanChange(t)}
                                disabled={actionLoading !== null}
                                aria-label={`Activate ${TIER_LABELS[t]}`}
                              >
                                {actionLoading === t ? "Starting..." : `Activate ${TIER_LABELS[t]}`}
                              </button>
                            ) : isEnterprise && isUpgrade ? (
                              <button
                                type="button"
                                className={styles["contact-sales-btn"]}
                                onClick={() => requestPlanChange(t)}
                                disabled={actionLoading !== null}
                              >
                                Contact Sales
                              </button>
                            ) : selfServicePlanChanges && isUpgrade ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                onClick={() => requestPlanChange(t)}
                                disabled={actionLoading !== null}
                                aria-label={`Upgrade to ${TIER_LABELS[t]}`}
                              >
                                {actionLoading === t ? "Starting..." : "Upgrade"}
                              </button>
                            ) : selfServicePlanChanges ? (
                              <button
                                type="button"
                                className={styles["select-plan-btn"]}
                                onClick={() => requestPlanChange(t)}
                                disabled={actionLoading !== null}
                                aria-label={`Downgrade to ${TIER_LABELS[t]}`}
                              >
                                Downgrade
                              </button>
                            ) : (
                              <span className={styles["plan-column-note"]}>
                                {externalBilling ? "Managed externally" : customStripeBilling ? "Custom billing" : "Unavailable"}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {enterprisePromptTier && (
        <div className={styles["enterprise-dialog-backdrop"]}>
          <div
            className={styles["enterprise-dialog"]}
            role="dialog"
            aria-modal="true"
            aria-labelledby="enterpriseSignupTitle"
            data-bs-backdrop="static"
          >
            <div className={styles["enterprise-dialog-title"]} id="enterpriseSignupTitle">
              Enterprise signup
            </div>
            <div className={styles["enterprise-dialog-body"]}>
              You can start Enterprise signup now. Our sales team will reach out to understand any additional integrations you need. Additional costs may apply if a required integration does not already exist.
            </div>
            <div className={styles["enterprise-dialog-actions"]}>
              <button
                type="button"
                className={styles["enterprise-dialog-secondary"]}
                onClick={() => setEnterprisePromptTier(null)}
                disabled={enterpriseSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles["enterprise-dialog-primary"]}
                onClick={() => void confirmEnterprisePlanChange()}
                disabled={enterpriseSubmitting}
              >
                {enterpriseSubmitting ? "Recording..." : "I understand, continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {planDialog && (
        <div className={styles["enterprise-dialog-backdrop"]}>
          <div
            className={styles["enterprise-dialog"]}
            role="dialog"
            aria-modal="true"
            aria-labelledby="planChangeDialogTitle"
            data-bs-backdrop="static"
          >
            <div className={styles["enterprise-dialog-title"]} id="planChangeDialogTitle">
              {planDialog.type === "cancel" && needsPlanActivation
                ? "Switch to FreeCrew?"
                : planDialog.type === "cancel"
                ? `Cancel ${TIER_LABELS[currentTier]} subscription?`
                : `Schedule downgrade to ${TIER_LABELS[planDialog.targetTier]}`}
            </div>
            <div className={styles["enterprise-dialog-body"]}>
              {planDialog.type === "cancel" && needsPlanActivation ? (
                <>
                Your tenant has {TIER_LABELS[currentTier]} access without an active Stripe subscription.
                Switching to FreeCrew will keep your data, but paid features will be disabled.
                </>
              ) : planDialog.type === "cancel" ? (
                <>
                Your {TIER_LABELS[currentTier]} subscription remains active until{" "}
                {formatDate(summaryPeriodEnd)}. After that, your account switches to FreeCrew.{" "}
                  You'll keep your data, but paid features will be disabled.
                </>
              ) : (
                `Your ${TIER_LABELS[currentTier]} subscription will remain active until the end of the current month (${formatDate(summaryPeriodEnd)}), then change to ${TIER_LABELS[planDialog.targetTier]}.`
              )}
            </div>
            <div className={styles["enterprise-dialog-actions"]}>
              <button
                type="button"
                className={styles["enterprise-dialog-secondary"]}
                onClick={() => setPlanDialog(null)}
                disabled={actionLoading !== null}
              >
                {planDialog.type === "cancel" ? `Keep ${TIER_LABELS[currentTier]}` : "Keep current plan"}
              </button>
              <button
                type="button"
                className={planDialog.type === "cancel" ? styles["danger-btn"] : styles["enterprise-dialog-primary"]}
                onClick={() => void confirmPlanDialog()}
                disabled={actionLoading !== null}
              >
                {planDialog.type === "cancel" && needsPlanActivation
                  ? "Switch to FreeCrew"
                  : planDialog.type === "cancel"
                    ? "Cancel Subscription"
                    : "Schedule month-end downgrade"}
              </button>
            </div>
          </div>
        </div>
      )}

      {paymentSetup && paymentSetupStripePromise && (
        <div className={styles["enterprise-dialog-backdrop"]}>
          <div
            className={`${styles["enterprise-dialog"]} ${styles["payment-dialog"]}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="paymentMethodDialogTitle"
            data-bs-backdrop="static"
          >
            <div className={styles["enterprise-dialog-title"]} id="paymentMethodDialogTitle">
              Update payment method
            </div>
            <div className={styles["enterprise-dialog-body"]}>
              <Elements
                stripe={paymentSetupStripePromise}
                options={{ clientSecret: paymentSetup.clientSecret }}
              >
                <PaymentMethodSetupForm
                  clientSecret={paymentSetup.clientSecret}
                  onCancel={() => setPaymentSetup(null)}
                  onError={(message) => setToast({ message, type: "error" })}
                  onSetupExpired={() =>
                    startPaymentMethodUpdate({ staleClientSecret: paymentSetup.clientSecret })
                  }
                  onSuccess={savePaymentMethod}
                />
              </Elements>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </MsalAuthenticationTemplate>
  );
};

export default Billing;
